import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import CareIcon from "@/CAREUI/icons/CareIcon";

import { Card } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { Avatar } from "@/components/Common/Avatar";

import query from "@/Utils/request/query";
import { formatName } from "@/Utils/utils";
import { Code } from "@/types/base/code/code";

import observationApi from "@/types/emr/observation/observationApi";
import { ObservationHistoryTable } from "./ObservationHistoryTable";

interface CodeGroup {
  codes: Code[];
  title: string;
  yAxisDomain?: [number, number];
  color?: string;
}

interface ObservationVisualizerProps {
  patientId: string;
  codeGroups: CodeGroup[];
  height?: number;
  gridCols?: number;
  encounterId: string;
}

interface ObservationDetails {
  value: number;
  enteredBy: string;
  enteredAt: string;
  note?: string;
  status: string;
}

interface ChartData {
  timestamp: string;
  time: number;
  values: Record<string, number>;
  details: Record<string, ObservationDetails>;
  [key: string]: unknown;
}

const DEFAULT_COLORS = [
  "#2563eb", // blue-600
  "#dc2626", // red-600
  "#16a34a", // green-600
  "#ea580c", // orange-600
  "#9333ea", // purple-600
  "#0d9488", // teal-600
  "#c026d3", // fuchsia-600
  "#ca8a04", // yellow-600
  "#0891b2", // cyan-600
] as const;

const formatChartDate = (
  dateString: string,
): { display: string; time: number } => {
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return {
    display: `${hours}:${minutes} ${day}/${month}/${year}`,
    time: date.getTime(),
  };
};

export const ObservationVisualizer = ({
  patientId,
  codeGroups,
  encounterId,
  height = 300,
  gridCols = 2,
}: ObservationVisualizerProps) => {
  const { t } = useTranslation();

  const allCodes = useMemo(
    () => codeGroups.flatMap((group) => group.codes),
    [codeGroups],
  );

  const queryKeyCodes = useMemo(
    () => allCodes.map((c) => c.code).join(","),
    [allCodes],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["observations", patientId, encounterId, queryKeyCodes],
    queryFn: query(observationApi.analyse, {
      pathParams: { patientId },
      queryParams: { encounter: encounterId },
      body: { codes: allCodes },
    }),
  });

  const processedDataByGroup = useMemo(() => {
    if (!data?.results?.length) return [];

    return codeGroups.map((group) => {
      const processedData: Record<string, ChartData> = {};
      const validCodeSet = new Set(group.codes.map((c) => c.code));

      group.codes.forEach((code) => {
        if (!validCodeSet.has(code.code) || !code.display) return;

        const resultGroup = data.results.find(
          (rg) => rg.code.code === code.code,
        );
        if (!resultGroup) return;

        resultGroup.results.forEach((observation) => {
          const rawTimestamp = observation.effective_datetime;
          if (!rawTimestamp || typeof rawTimestamp !== "string") return;

          const value = Number(observation.value?.value);
          if (isNaN(value)) return;

          if (!processedData[rawTimestamp]) {
            const { display, time } = formatChartDate(rawTimestamp);
            processedData[rawTimestamp] = {
              timestamp: display,
              time,
              values: {},
              details: {},
            };
          }

          const entry = processedData[rawTimestamp];
          entry[code.display] = value;
          entry.values[code.display] = value;
          entry.details[code.display] = {
            value,
            enteredBy: formatName(observation.data_entered_by),
            enteredAt: formatChartDate(rawTimestamp).display,
            note: observation.note || undefined,
            status: observation.status,
          };
        });
      });

      const sortedData = Object.values(processedData).sort(
        (a, b) => a.time - b.time,
      );

      return {
        ...group,
        data: sortedData,
      };
    });
  }, [codeGroups, data]);

  const gridClass =
    gridCols === 1 ? "grid-cols-1" : "grid-cols-1 xl:grid-cols-2";

  if (isLoading) {
    return (
      <div className={`grid gap-4 ${gridClass}`}>
        {codeGroups.map((_, index) => (
          <Card key={index} className="p-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-[250px]" />
              <Skeleton className="w-full" style={{ height: `${height}px` }} />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (!data?.results?.length) {
    return (
      <div className={`grid gap-4 ${gridClass}`}>
        {codeGroups.map((_, index) => (
          <Card key={index} className="p-4">
            <div
              className="flex items-center justify-center text-gray-500 text-sm"
              style={{ height: `${height}px` }}
            >
              {t("no_data_available")}
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className={`grid gap-4 ${gridClass}`}>
      {processedDataByGroup.map((group, groupIndex) => (
        <Card key={groupIndex} className="p-4 overflow-hidden">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-medium">{group.title}</h3>
              <Popover>
                <PopoverTrigger className="!px-0">
                  <CareIcon
                    icon="l-info-circle"
                    className="size-4 text-gray-500 hover:text-gray-700 cursor-pointer"
                  />
                </PopoverTrigger>
                <PopoverContent
                  className="max-w-fit w-[calc(100vw-2rem)] sm:max-w-fit sm:w-auto break-words p-3"
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  collisionPadding={16}
                >
                  <div className="space-y-1.5">
                    <div className="text-xs font-semibold text-gray-700">
                      Observations:
                    </div>
                    {group.codes.map((code) => (
                      <div key={code.code} className="text-xs text-gray-600">
                        <span className="font-medium">{code.display}</span> (
                        {code.code})
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <Tabs defaultValue="graph" className="w-full">
            <TabsList className="flex w-full h-auto p-1 bg-muted rounded-lg gap-1">
              <TabsTrigger
                value="graph"
                className="flex-1 shrink-0 text-xs 2xl:text-sm px-2 py-1.5 whitespace-nowrap"
              >
                {t("graph")}
              </TabsTrigger>
              <TabsTrigger
                value="data"
                className="flex-1 shrink-0 text-xs 2xl:text-sm px-2 py-1.5 whitespace-nowrap"
              >
                {t("recent_data")}
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="flex-1 shrink-0 text-xs 2xl:text-sm px-2 py-1.5 whitespace-nowrap"
              >
                {t("full_history")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="graph">
              <div style={{ height: `${height}px` }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={group.data}
                    margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      type="number"
                      domain={["dataMin", "dataMax"]}
                      scale="time"
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getHours().toString().padStart(2, "0")}:${date
                          .getMinutes()
                          .toString()
                          .padStart(2, "0")}`;
                      }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      domain={group.yAxisDomain || ["auto", "auto"]}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      labelFormatter={(value) => {
                        if (typeof value === "number") {
                          return formatChartDate(new Date(value).toISOString())
                            .display;
                        }
                        return value;
                      }}
                    />
                    <Legend />
                    {group.codes.map((code, codeIndex) => {
                      if (!code.display) return null;
                      return (
                        <Line
                          key={code.code}
                          type="monotone"
                          dataKey={code.display}
                          stroke={
                            group.color ||
                            DEFAULT_COLORS[codeIndex % DEFAULT_COLORS.length]
                          }
                          dot={true}
                          connectNulls
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="data">
              <div className="max-h-[400px] overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Values</TableHead>
                      <TableHead>Entered By</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.data.map((row) => {
                      const observations = group.codes
                        .map((code) => {
                          if (!code.display) return null;
                          const details = row.details[code.display];
                          if (!details) return null;
                          return { code, details };
                        })
                        .filter((x): x is NonNullable<typeof x> => x !== null);

                      if (observations.length === 0) return null;

                      return (
                        <TableRow key={row.timestamp}>
                          <TableCell className="font-mono text-xs whitespace-nowrap">
                            {row.timestamp}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {observations.map(({ code, details }) => (
                                <div
                                  key={code.code}
                                  className="flex items-center gap-2 text-sm whitespace-nowrap"
                                >
                                  <span className="font-medium text-gray-700">
                                    {code.display}:
                                  </span>
                                  <span>{details.value}</span>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 whitespace-nowrap">
                              <Avatar
                                name={observations[0].details.enteredBy}
                                className="size-6 text-xs"
                              />
                              <span className="text-sm">
                                {observations[0].details.enteredBy}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {observations.map(
                                ({ code, details }) =>
                                  details.note && (
                                    <div
                                      key={code.code}
                                      className="text-xs text-gray-500"
                                    >
                                      <span className="font-medium">
                                        {code.display}:
                                      </span>{" "}
                                      {details.note}
                                    </div>
                                  ),
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="history">
              <ObservationHistoryTable
                patientId={patientId}
                encounterId={encounterId}
                codes={group.codes}
              />
            </TabsContent>
          </Tabs>
        </Card>
      ))}
    </div>
  );
};
